import React, { useState, useEffect } from 'react';
import { X, ShoppingCart } from 'lucide-react';
import { Link } from 'react-router-dom';

interface CartNotificationProps {
  product: {
    name: string;
    image: string;
  };
  isVisible: boolean;
  onClose: () => void;
}

export function CartNotification({ product, isVisible, onClose }: CartNotificationProps) {
  const [isEntering, setIsEntering] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);

  const NOTIFICATION_DURATION = 5000; // 5 seconds
  const ANIMATION_DURATION = 300; // 300ms for animations

  useEffect(() => {
    if (isVisible) {
      // Start the entrance animation
      setShouldRender(true);
      setIsExiting(false);
      // Small delay to ensure the element is in DOM before animating
      setTimeout(() => {
        setIsEntering(true);
      }, 10);

      // Auto-close after duration
      const closeTimeout = setTimeout(() => {
        handleClose();
      }, NOTIFICATION_DURATION);

      return () => clearTimeout(closeTimeout);
    }
  }, [isVisible]);

  const handleClose = () => {
    // Start exit animation
    setIsEntering(false);
    setIsExiting(true);
    
    // Remove from DOM after animation completes
    setTimeout(() => {
      setShouldRender(false);
      setIsExiting(false);
      onClose();
    }, ANIMATION_DURATION);
  };

  if (!shouldRender) return null;

  return (
    <div 
      className={`
        fixed bottom-4 right-4 z-50 transform transition-all duration-300 ease-out
        ${isEntering && !isExiting ? 'translate-x-0 opacity-100 scale-100' : ''}
        ${!isEntering || isExiting ? 'translate-x-full opacity-0 scale-95' : ''}
      `}
      style={{
        transitionProperty: 'transform, opacity, scale',
        transitionDuration: '300ms',
        transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)'
      }}
    >
      <div className="bg-white rounded-lg shadow-xl p-4 max-w-sm border border-[#4FC1E9]/20 overflow-hidden">
        {/* Animated progress bar */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gray-100 rounded-t-lg overflow-hidden">
          <div 
            className={`h-full bg-[#4FC1E9] rounded-t-lg transition-all duration-100 ${
              isEntering && !isExiting ? 'animate-shrink-progress' : 'w-full'
            }`}
            style={{
              animation: isEntering && !isExiting ? `shrinkProgress ${NOTIFICATION_DURATION}ms linear forwards` : 'none'
            }}
          />
        </div>

        <div className="flex items-center gap-3 mt-1">
          {/* Product image with subtle animation */}
          <div className={`transition-transform duration-500 ${isEntering ? 'scale-100' : 'scale-90'}`}>
            <img
              src={product.image}
              alt={product.name}
              title={product.name}
              className="w-16 h-16 object-cover rounded shadow-sm"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                if (!target.src.includes('https://res.cloudinary.com/daka2lob2/image/upload/v1751272740/placeholder-image_d7qyxq.jpg')) {
                  target.src = 'https://res.cloudinary.com/daka2lob2/image/upload/v1751272740/placeholder-image_d7qyxq.jpg';
                }
              }}
            />
          </div>

          <div className="flex-1">
            {/* Success message with staggered animation */}
            <div className={`transition-all duration-300 delay-100 ${
              isEntering ? 'translate-y-0 opacity-100' : 'translate-y-2 opacity-0'
            }`}>
              <p className="text-sm font-medium text-gray-900 mb-1">{product.name}</p>
              <p className="text-sm text-[#4FC1E9] mb-3 flex items-center">
                <span className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></span>
                Lisatud ostukorvi
              </p>
            </div>

            {/* Action button with delayed animation */}
            <div className={`transition-all duration-300 delay-200 ${
              isEntering ? 'translate-y-0 opacity-100' : 'translate-y-2 opacity-0'
            }`}>
              <Link 
                to="/ostukorv"
                className="inline-flex items-center text-sm bg-[#4FC1E9] text-white px-3 py-1.5 rounded-full hover:bg-[#3ba8d0] transition-colors duration-200 transform hover:scale-105"
              >
                <ShoppingCart className="w-4 h-4 mr-1" />
                Mine ostukorvi
              </Link>
            </div>
          </div>

          {/* Close button with hover animation */}
          <button 
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 self-start transition-all duration-200 hover:scale-110 hover:rotate-90 p-1 rounded-full hover:bg-gray-100"
            aria-label="Sulge teatis"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
}
